import "@/types/Object.extensions";
import { CloneScenarioRequest, Co2MaterialSettingType, CopyModelMaterialRequest, ProblemDetails, RebarEstimationStatus, Scenario, ScenarioMetadata, ScenarioModel } from "@api/apiClient";
import { useDeleteScenario, useScenarios, useUpdateScenario,useEnableScenarioFinal } from "@api/scenarios";
import { GridToolbarCustom } from "@components/DataGrid/components/toolbar/GridToolbarCustom";
import DataGrid from "@components/DataGrid/DataGrid";
import { sumWithNullsAgg } from "@components/DataGrid/utils/aggregation";
import useRowSelectionModel from "@features/analysis/hooks/useSelectionModel";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, Snackbar, useTheme } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import {
  GRID_AGGREGATION_FUNCTIONS,
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridGroupNode,
  GridRowId,
  GridRowSelectionPropagation,
} from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import _ from "lodash";
import { useConfirm } from "material-ui-confirm";
import { forwardRef, MutableRefObject, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useResizeObserver from "use-resize-observer";
import CloneScenarioForm from "./CloneScenarioForm";
import CopyModelMaterilForm from "./CopyModelMaterialForm";
import { getColumns, getFilteredColumns, getGroupingColumnDef } from "./ScenariosGrid.columns";
import { useAppSettings } from "@api/appSettings";
import ScenarioSettingsDialog from "./ScenarioSettingsDialog";

export interface ScenarioGridProps {
  apiRef: MutableRefObject<GridApiPremium>;
}

const gridStorageKey = "scenarios-grid";

const defaultInitialState: GridInitialStatePremium = {
  density: "compact",
  aggregation: {
    model: {
      a1A2A3: "sum",
      a4A5: "sum",
      a: "sum",
      b1: "sum",
      b2B5: "sum",
      biogenic: "sum",
      b: "sum",
      c: "sum",
      d: "sum",
      totalCo2: "sum",
      percentRebar: "avg",
      percentAssigned: "avg",
    },
  },
  pinnedColumns: {
    left: [GRID_CHECKBOX_SELECTION_FIELD, GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, GRID_TREE_DATA_GROUPING_FIELD],
  },
};

const rowSelectionPropagation: GridRowSelectionPropagation = {
  parents: true,
  descendants: true,
};

const slots = {
  toolbar: GridToolbarCustom,
};

const slotProps = {
  toolbar: {
    showQuickFilter: false,
    showSearchFilter: false,
    printOptions: { disableToolbarButton: true },
    csvOptions: { disableToolbarButton: true },
    excelOptions: { disableToolbarButton: true },
    headerFiltersOptions: { disableToolbarButton: true },
  },
};

const getRowId = (row: any) => {
  try {
    return row.key;
  } catch (error) {
    console.error("getRowId error: ", error);
  }
};

const getTreeDataPath = (row: any) => {
  return row.path;
};

const ScenariosGridRef = forwardRef<any, PropsWithChildren<ScenarioGridProps>>((props, ref) => {
  const { apiRef } = props;
  const appSettings = useAppSettings();
  const appStore = useAppStore();
  const theme = useTheme();
  const { id: projectId } = useParams();
  const [scenarioId, setScenarioId] = useState<string | undefined>();
  const [enableFinal, setEnableFinal] = useState<boolean | undefined>();
  const [key, setKey] = useState(0); // increment key to force re-initialize of the grid
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [gridHeight, setGridHeight] = useState<string | null>(null);
  const containerRef = useRef<any>(null);
  const [openCloneScenario, setOpenCloneScenario] = useState(false);
  const [openCopyModelMaterial, setOpenCopyModelMaterial] = useState(false);
  const [openScenarioDialog, setOpenScenarioDialog] = useState(false);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
  const [scenarioDialogMode, setScenarioDialogMode] = useState<"edit" | "settings">("edit");
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedScenarioIdForEdit, setSelectedScenarioIdForEdit] = useState<string | null>(null);
  const [cloneScenarioFormData, setCloneScenarioFormData] = useState<CloneScenarioRequest>(
    new CloneScenarioRequest({
      scenarioId: "",
      name: "",
      description: "",
      rebarEstimation: false,
      profileId: "",
    }),
  );
  const [copyModelMaterialData, setCopyModelMaterialData] = useState<CopyModelMaterialRequest>(
    new CopyModelMaterialRequest({
      dstModelId: "",
      dstScenarioId: "",
      srcScenarioId: "",
      srcModelId: "",
      modelName: "",
    }),
  );
  const deleteScenario = useDeleteScenario(projectId!);
  const enableScenarioFinal = useEnableScenarioFinal();
  const scenarios = useScenarios(projectId!);
  const updateScenario = useUpdateScenario();
  const confirm = useConfirm();
  const { handelRowSelectionModelChange, selectionModel } = useRowSelectionModel(apiRef);

  const handleRowSelectionChange = async (selection: any) => {
    console.log("onSelectionChange before: ", selection);

    // GridRowSelectionModel is readonly so we need to convert it to a mutable array
    const { models, newRowSelectionModel } = await handelRowSelectionModelChange(selection as GridRowId[]);

    console.log("onSelectionChange after: ", selection);

    _.delay(async () => {
      for (const item of models) {
        await appStore.selectionManager.selectModels(item.data.scenarioId, item.data.model.apsViewId, true);
      }
    }, 100);
  };

  const removeScenarios = async (scenarioId: any) => {
    if (scenarioId) {
      await deleteScenario.mutateAsync(scenarioId as any);
      setKey(prev => prev + 1);
    }
  };

  const deleteConfirm = async () => {
    const { confirmed } = await confirm({
      title: i18n.t("scenarios-grid.delete-dialog.header"),
      description: i18n.t("scenarios-grid.delete-dialog.message"),
      confirmationText: i18n.t("dialog.yes"),
      cancellationText: i18n.t("dialog.cancel"),
      dialogProps: {
        maxWidth: "xs",
      },
      confirmationButtonProps: {
        variant: "contained",
        color: "error",
      },
      cancellationButtonProps: {
        variant: "outlined",
      },
    });
    return confirmed;
  };
  
   // setNotifyMessage(i18n.t("app.job-complete-event.message", { name: job.model?.apsItemName }));
    const enableFinalConfirm = async (existingScenarioName: Scenario, newScenarioName: string) => {
    const { confirmed } = await confirm({
      title: i18n.t("scenarios-grid.enable-projectPhase-final-dialog.header"),
      description: i18n.t("scenarios-grid.enable-projectPhase-final-dialog.message", {
        existingScenario: existingScenarioName.name,
        newScenario: newScenarioName,
        projectPhase: existingScenarioName.projectPhase?.name,
      }),
      confirmationText: i18n.t("dialog.yes"),
      cancellationText: i18n.t("dialog.cancel"),
      dialogProps: {
        maxWidth: "xs",
      },
      confirmationButtonProps: {
        variant: "contained",
        color: "warning",
      },
      cancellationButtonProps: {
        variant: "outlined",
      },
    });
  return confirmed;
};

  const handleDeleteScenario = async (row: any) => {
    const confirmed = await deleteConfirm();
    if (confirmed) {
      removeScenarios(row.key);
    }
  };

  const handleScenarioEnableFinal = async (event: React.ChangeEvent<HTMLInputElement>, row: any) => {
    const isEnabling = event.target.checked;

    // Get all rows from the grid that are scenarios (not models)
    const scenarioRows = apiRef.current
      .getAllRowIds()
      .map((id) => apiRef.current.getRow(id))
      .filter((row) => row?.type === "scenario");

    // Check if there's already a final scenario when trying to enable
    if (isEnabling) {
      const newScenario = (scenarioRows as any)?.find((s: any) => s.key === row.key);
      const existingFinalScenario = (scenarioRows as any)?.find(
        (s: any) => s.data.isFinalForProjectPhase && s.data.projectPhaseId === newScenario?.data.projectPhaseId && s.data.scenarioId !== row.key,
      );

      if (existingFinalScenario) {
        const userConfirmed = await enableFinalConfirm(existingFinalScenario.data, newScenario ? (newScenario.name as string) : "the selected scenario");

        if (!userConfirmed) {
          // User cancelled - revert the checkbox by updating the row
          apiRef.current.updateRows([
            {
              ...row,
              data: {
                ...row.data,
                isFinalForProjectPhase: false,
              },
            },
          ]);
          return;
        }
      }
    }

    // Proceed with the API call
      try {
        await enableScenarioFinal.mutateAsync({
          scenarioId: row.key,
          enableFinal: isEnabling,
        } as any);

        await scenarios.refetch();
      } catch (error) {
        console.error("Error updating scenario final status:", error);
        if(error && (error as any)['details']!=undefined)
        {
          setShowErrorMessage(true)
          setErrorMessage((error as any)['details'] || "An error occurred.");
        }
        else
        {
          setShowErrorMessage(true);
          setErrorMessage("Failed to update scenario metadata. Please try again.");
        }
      
      // Revert on error
      apiRef.current.updateRows([
        {
          ...row,
          data: {
            ...row.data,
            isFinalForProjectPhase: !isEnabling,
          },
        },
      ]);
    }
  };
  const handleCloneScenario = (row: any) => {
    const isProfileBased = appSettings.data?.profileLevelRebarSettings;
    const rebarEstimation = isProfileBased
      ? row.data.profile?.rebarEstimationStatus === RebarEstimationStatus.Enabled || row.data.profile?.rebarEstimationStatus === RebarEstimationStatus.Estimated
      : row.data.rebarEstimationStatus === RebarEstimationStatus.Enabled || row.data.rebarEstimationStatus === RebarEstimationStatus.Estimated;

    const cloneScenarioObject = new CloneScenarioRequest({
      scenarioId: row.data.scenarioId,
      name: row.data.name,
      description: row.data.description,
      rebarEstimation,
      profileId: row.data.profileId
    });
    setCloneScenarioFormData(cloneScenarioObject);
    setOpenCloneScenario(true);
  };

  const handleCloneScenarioClose = () => {
    setCloneScenarioFormData(
      new CloneScenarioRequest({
        scenarioId: "",
        name: "",
        description: "",
        rebarEstimation: false,
        profileId: "",
      }),
    );
    setOpenCloneScenario(false);
  };

  const handleCopyModelMaterials = (row: any) => {
    const copyModelmaterialObject = new CopyModelMaterialRequest({
      dstModelId: row.data.modelId,
      dstScenarioId: row.data.scenarioId,
      modelName: row.data.name,
    });
    setCopyModelMaterialData(copyModelmaterialObject);
    setOpenCopyModelMaterial(true);
  };

  const handleCopyModelMaterialClose = () => {
    setCopyModelMaterialData(
      new CopyModelMaterialRequest({
        dstModelId: "",
        dstScenarioId: "",
        srcScenarioId: "",
        srcModelId: "",
        modelName: "",
      }),
    );
    setOpenCopyModelMaterial(false);
  };

  const updateGridHeight = (containerHeight: number) => {
    const padding = 8;
    const newGridHeight = `${containerHeight - padding}px`;
    console.log(`newGridHeight: ${newGridHeight}`);
    setGridHeight(newGridHeight);
  };

  useResizeObserver({
    ref: containerRef,
    onResize: ({ height }: { height: number | undefined }) => {
      if (height && height !== containerHeight) {
        setContainerHeight(height);
        updateGridHeight(height);
      }
    },
  });

  const sx = useMemo(() => {
    return {
      height: gridHeight,
      maxHeight: gridHeight,
    };
  }, [gridHeight]);

  const handleEditScenario = (row: any) => {
    setScenarioDialogMode("edit");
    setSelectedScenarioId(row.key);
    setOpenScenarioDialog(true);
  };

  const handleScenarioSettings = (row: any) => {
    setScenarioDialogMode("settings");
    setSelectedScenarioId(row.key);
    setOpenScenarioDialog(true);
  };

  const handleScenarioDialogClose = () => {
    setOpenScenarioDialog(false);
    setSelectedScenarioId(null);
  };

  const handleScenarioSettingsSave = async (updateRequest: any) => {
    try {
      await updateScenario.mutateAsync(updateRequest);
      await scenarios.refetch();
    } catch (error) {
      console.error("Error updating scenario:", error);
      throw error;
    }
  };

  useEffect(() => {
    if (appStore.scenarioName || appStore.modelName) {
      scenarios.refetch();
    }
  }, [appStore.scenarioName, appStore.modelName]);

  useEffect(() => {
    const newRows: any = [];

    scenarios.data?.forEach((scenario: Scenario, index: number) => {
      let percentOfEmissionReductionTarget = ((scenario.totalCo2 || 0) / (appStore.project?.projectEmissionReductionTarget || 0)) * 100;
      percentOfEmissionReductionTarget = _.isFinite(percentOfEmissionReductionTarget) ? percentOfEmissionReductionTarget : 0;
      const scenarioRow = {
        index,
        key: scenario.scenarioId,
        path: [scenario.scenarioId],
        type: "scenario",
        data: {
          ...scenario,
          tag: `S${index + 1}`,
          percentOfEmissionReductionTarget,
          percentAssigned: scenario.getPercentageAssigned(),
        },
      };
      newRows.push(scenarioRow);

      scenario.scenarioModels?.forEach((scenarioModel: ScenarioModel, modelIndex: number) => {
        percentOfEmissionReductionTarget = ((scenarioModel.totalCo2 || 0) / (scenarioModel.model?.modelEmissionReductionTarget || 0)) * 100;
        percentOfEmissionReductionTarget = _.isFinite(percentOfEmissionReductionTarget) ? percentOfEmissionReductionTarget : 0;
        const model = {
          index: modelIndex,
          key: scenarioModel.scenarioModelId,
          path: [scenario.scenarioId, scenarioModel.scenarioModelId],
          type: "model",
          data: {
            ...scenarioModel,
            tag: `S${index + 1}-M${modelIndex + 1}`,
            name: scenarioModel.model?.modelName,
            totalCo2: scenarioModel.totalCo2, // TODO: must subtract the totalCo2 of the rebar and non-modelled elements
            percentOfEmissionReductionTarget: undefined, //percentOfEmissionReductionTarget,
            percentAssigned: scenarioModel.percentageAssign,
          },
        };
        newRows.push(model);
      });
    });

    console.log("rows: ", newRows);

    apiRef.current?.updateRows?.(newRows || []);
  }, [apiRef.current?.updateRows, scenarios.data]);

  const groupingColumnDef = useMemo(() => getGroupingColumnDef(i18n.t("scenarios-grid.columns.grouping")), [apiRef]);
    const columns = useMemo(() => getColumns(apiRef, appStore, theme, { handleCloneScenario, handleDeleteScenario, handleCopyModelMaterials, handleEditScenario, handleScenarioSettings, handleScenarioEnableFinal }, appSettings), [apiRef, appStore, theme]);

  const { filteredColumns, columnVisibilityModel: initialVisibilityModel } = useMemo(() => getFilteredColumns(columns, appStore), [columns, appStore]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(initialVisibilityModel);

  const initialState = useMemo(
    () => ({
      ...defaultInitialState,
      columns: {
        columnVisibilityModel,
      },
      headerFilters: true, // this needs to be sync'd with DataGridContext.headerFilters
    }),
    [columnVisibilityModel],
  );
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const handleCloseErrorMessage = () => {
    setShowErrorMessage(false);
  };
  const getAggregatePosition = useCallback((groupNode: GridGroupNode) => {
    return groupNode.depth === -1 ? null : "inline";
  }, []);

  const aggregationFunctions = useMemo(() => {
    return {
      ...GRID_AGGREGATION_FUNCTIONS,
      sum: sumWithNullsAgg,
    };
  }, []);

  console.log(initialState);
  console.log(filteredColumns);
  console.log(apiRef.current?.state?.rows);

  // TODO: add percentRebar column feature flagged
  // (appStore.scenario?.rebarEstimationStatus === RebarEstimationStatus.Enabled || appStore.scenario?.rebarEstimationStatus === RebarEstimationStatus.Estimated)
  // TODO: add column buttons
  return (
    <>
      <Box ref={containerRef} sx={{ display: "flex", flexGrow: 1, flexDirection: "column", width: "100%" }}>
        {gridHeight && (
          <DataGrid
            key={key}
            ref={ref}
            apiRef={apiRef}
            treeData
            autosizeColumns
            autosizeTreeDataGroupingColumns
            checkboxSelection
            disableRowSelectionOnClick
            getTreeDataPath={getTreeDataPath}
            columns={filteredColumns}
            rowSelectionPropagation={rowSelectionPropagation}
            initialState={initialState}
            groupingColDef={groupingColumnDef}
            getRowId={getRowId}
            slots={slots}
            slotProps={slotProps}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={handleRowSelectionChange}
            loading={scenarios.isFetching}
            aggregationFunctions={aggregationFunctions}
            getAggregationPosition={getAggregatePosition}
            hideFooter
            //disableDensitySelector
            disableColumnFilter
            //disableColumnMenu
            //disableColumnSorting
            //disableMultipleColumnsSorting
            //disableColumnReorder
            //disableColumnPinning
            storageKey={gridStorageKey}
          />
        )}

        <Dialog
          fullWidth={true}
          maxWidth={"sm"}
          open={openCloneScenario}
          onClose={handleCloneScenarioClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            id="alert-dialog-title"
          >
            {i18n.t("scenarios-grid.clone-dialog.title")}
            <IconButton onClick={handleCloneScenarioClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <CloneScenarioForm
              formData={{
                scenarioId: cloneScenarioFormData.scenarioId as any,
                name: cloneScenarioFormData.name as any,
                description: cloneScenarioFormData.description as any,
                rebarEstimation: cloneScenarioFormData.rebarEstimation as any,
                profileId: cloneScenarioFormData.profileId as any,
              }}
              scenarios={scenarios.data as any}
              handleCloneScenarioClose={handleCloneScenarioClose}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          fullWidth={true}
          maxWidth={"sm"}
          open={openCopyModelMaterial}
          onClose={handleCopyModelMaterialClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            id="alert-dialog-title"
          >
            {i18n.t("scenarios-grid.copy-modal-materials-dialog.title")}
            <IconButton onClick={handleCopyModelMaterialClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <CopyModelMaterilForm
              copyScenarioId={copyModelMaterialData.dstScenarioId as any}
              copyModelId={copyModelMaterialData.dstModelId as any}
              scenarios={scenarios.data as any}
              modelName={copyModelMaterialData.modelName as any}
              handleCopyModelMaterialClose={handleCopyModelMaterialClose}
            />
          </DialogContent>
        </Dialog>

        <ScenarioSettingsDialog open={openScenarioDialog} scenarioId={selectedScenarioId} projectId={projectId} mode={scenarioDialogMode} onClose={handleScenarioDialogClose} onSave={handleScenarioSettingsSave} />
      </Box>

      <Snackbar open={showErrorMessage} autoHideDuration={5000} onClose={handleCloseErrorMessage} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleCloseErrorMessage} severity="error" variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );

});

const ScenariosGrid = ScenariosGridRef;
export default ScenariosGrid;
